import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Loading, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ListChecks, Image, MapPinOff, MapPin, Check, X } from "lucide-react";
import { toast } from "sonner";
import { onErr, openImage, num } from "@/lib/providers";

export default function FieldLogsTab() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("Submitted");
  const [projectId, setProjectId] = useState("all");
  const [deciding, setDeciding] = useState(null);   // { log, decision }
  const [units, setUnits] = useState("");
  const [remark, setRemark] = useState("");

  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const fieldsFor = (pid) => (projects || []).find((p) => p.id === pid)?.log_fields || [];
  const { data: logs, isLoading } = useQuery({
    queryKey: ["fieldLogs", status, projectId],
    queryFn: async () => (await api.get("/field-logs", { params: {
      ...(status !== "all" ? { status } : {}), ...(projectId !== "all" ? { project_id: projectId } : {}) } })).data,
  });

  const decide = useMutation({
    mutationFn: async () => (await api.post(`/field-logs/${deciding.log.id}/decide`, {
      decision: deciding.decision, verified_units: deciding.decision === "Verified" ? Number(units) : null, remark,
    })).data,
    onSuccess: () => {
      toast.success(deciding.decision === "Verified" ? "Log verified" : "Log rejected");
      qc.invalidateQueries({ queryKey: ["fieldLogs"] }); qc.invalidateQueries({ queryKey: ["workOrders"] });
      setDeciding(null);
    },
    onError: onErr,
  });

  const open = (log, decision) => { setDeciding({ log, decision }); setUnits(String(log.units_completed)); setRemark(""); };
  const partial = deciding?.decision === "Verified" && Number(units) < (deciding?.log.units_completed || 0);

  return (
    <div>
      <div className="flex flex-wrap gap-3 items-end mb-4">
        <div className="w-44">
          <Label>Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="Submitted">To verify</SelectItem><SelectItem value="Verified">Verified</SelectItem>
              <SelectItem value="Rejected">Rejected</SelectItem><SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-60">
          <Label>Project</Label>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All projects</SelectItem>
              {(projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-slate-500 ml-auto max-w-sm">Check the photos and GPS before verifying. You can verify fewer units than claimed, with a remark. Only verified units are paid.</p>
      </div>
      {isLoading ? <Loading /> : !logs?.length ? (
        <EmptyState icon={ListChecks} title="Nothing here" hint={status === "Submitted" ? "No field logs waiting for verification." : "No logs match this filter."} />
      ) : (
        <Card className="border-slate-200 shadow-none overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              {["Date", "Provider", "Area", "Claimed", "Details", "GPS", "Photos", "Status", ""].map((h) => <TableHead key={h} className="overline text-slate-500">{h}</TableHead>)}
            </TableRow></TableHeader>
            <TableBody>
              {logs.map((l) => (
                <TableRow key={l.id}>
                  <TableCell className="whitespace-nowrap text-sm">{l.log_date}</TableCell>
                  <TableCell className="font-medium text-navy">{l.provider_name}<div className="text-xs text-slate-400">{l.provider_code} · {l.wo_number}</div></TableCell>
                  <TableCell className="text-sm max-w-[200px]">{l.area}{l.notes && <div className="text-xs text-slate-400 truncate" title={l.notes}>{l.notes}</div>}</TableCell>
                  <TableCell className="text-sm font-semibold">{num(l.units_completed)}{l.verified_units != null && l.verified_units !== l.units_completed && <div className="text-xs text-amber-700">verified {num(l.verified_units)}</div>}</TableCell>
                  <TableCell className="text-xs text-slate-600">
                    {fieldsFor(l.project_id).map((f) => <div key={f.key}>{f.label}: {num((l.extra || {})[f.key])}</div>)}
                  </TableCell>
                  <TableCell className="text-xs">
                    {l.outside_city
                      ? <span className="text-red-600 inline-flex items-center gap-1" title={`${l.latitude}, ${l.longitude}`}><MapPinOff className="h-3.5 w-3.5" />{l.distance_from_centre_km} km out</span>
                      : <a className="text-emerald-700 inline-flex items-center gap-1 hover:underline" target="_blank" rel="noreferrer" href={`https://maps.google.com/?q=${l.latitude},${l.longitude}`}><MapPin className="h-3.5 w-3.5" />In city</a>}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {Array.from({ length: l.photo_count || 0 }).map((_, i) => (
                      <Button key={i} size="sm" variant="ghost" title={`Photo ${i + 1}`} onClick={() => openImage(`/field-logs/${l.id}/photo/${i}`)}><Image className="h-4 w-4" /></Button>
                    ))}
                  </TableCell>
                  <TableCell><StatusBadge status={l.status} />{l.decision_remark && <div className="text-[11px] text-slate-400 max-w-[140px]">{l.decision_remark}</div>}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {l.status === "Submitted" && <>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 mr-1" onClick={() => open(l, "Verified")}><Check className="h-4 w-4" /></Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => open(l, "Rejected")}><X className="h-4 w-4" /></Button>
                    </>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={!!deciding} onOpenChange={(o) => !o && setDeciding(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>{deciding?.decision === "Verified" ? "Verify" : "Reject"} log — {deciding?.log.area}, {deciding?.log.log_date}</DialogTitle></DialogHeader>
          {deciding?.decision === "Verified" && (
            <div><Label>Verified units (claimed {num(deciding.log.units_completed)})</Label>
              <Input type="number" className="mt-1" value={units} min={0} max={deciding.log.units_completed} onChange={(e) => setUnits(e.target.value)} /></div>
          )}
          <div><Label>Remark {deciding?.decision === "Rejected" || partial ? "*" : "(optional)"}</Label><Input className="mt-1" value={remark} onChange={(e) => setRemark(e.target.value)} /></div>
          {deciding?.log.outside_city && <p className="text-xs text-red-600">GPS was {deciding.log.distance_from_centre_km} km from the city centre — outside the project boundary.</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeciding(null)}>Back</Button>
            <Button className="bg-navy hover:bg-navy-700"
              disabled={decide.isPending || ((deciding?.decision === "Rejected" || partial) && !remark.trim())}
              onClick={() => decide.mutate()}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
